import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { SpiderIcon } from "@/components/SpiderIcon";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [again, setAgain] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== again) {
      setError("The two passwords are not the same.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset", { token, password });
      toast.success("Password changed. You can sign in now.");
      navigate("/login");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <form onSubmit={submit} className="w-full max-w-sm space-y-4" data-testid="reset-form">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-10 w-10 grid place-items-center border border-primary/40 bg-primary/10 rounded-sm">
            <SpiderIcon className="h-6 w-6 text-primary" />
          </div>
          <div>
            <div className="text-lg font-semibold tracking-tight">TradeCrawler</div>
            <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-muted-foreground">Choose a new password</div>
          </div>
        </div>
        {!token && (
          <div className="text-xs text-sell font-mono border border-sell/40 bg-sell/5 rounded-sm px-3 py-2">
            This page needs the link from your reset email.
          </div>
        )}
        <div>
          <Label className="text-[10px] font-mono uppercase text-muted-foreground">New password</Label>
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password" className="h-10 rounded-sm mt-1" data-testid="reset-password-input" />
        </div>
        <div>
          <Label className="text-[10px] font-mono uppercase text-muted-foreground">Type it again</Label>
          <Input type="password" value={again} onChange={(e) => setAgain(e.target.value)}
            autoComplete="new-password" className="h-10 rounded-sm mt-1" data-testid="reset-password-again" />
        </div>
        {error && (
          <div className="flex items-center gap-2 text-xs text-sell font-mono border border-sell/40 bg-sell/5 rounded-sm px-3 py-2" data-testid="reset-error">
            <TriangleAlert className="h-3.5 w-3.5" /> {error}
          </div>
        )}
        <Button type="submit" disabled={loading || !token} className="w-full h-10 rounded-sm" data-testid="reset-submit-btn">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save new password"}
        </Button>
        <p className="text-[10px] text-muted-foreground font-mono">
          <Link to="/login" className="underline">Back to sign in</Link>
        </p>
      </form>
    </div>
  );
}
